/**
 * Gradient fill math + texture strip (Faz 7.6 — OCS hatch_model.rs +
 * hatch.wgsl shader profiles parity).
 *
 * 5 kinds x inverted = the 9/10 user patterns of the OCS gradient editor:
 *   Linear | Cylinder | Spherical | Hemispherical | Curved (+ INV prefix)
 *
 * Profile math per the OCS fragment shader:
 * - Linear family (linear/cylindrical/curved): the position projects onto
 *   the grad_cos/grad_sin axis; t = clamp((proj-min)/range), then
 *   cylindrical (k==1) folds to 1-|2t-1|, curved (k==4) squares to t².
 * - Radial family (spherical/hemispherical): t = clamp(d/range) from the
 *   center, hemispherical applies √t.
 * - inverted → 1-t.
 * - Color order follows the shader: linear mixes color→color2 over t;
 *   radial mixes color2→color (outside-inside: color1 at the rim, color2
 *   at the center).
 */

import type { Pt3 } from '../core/commands/geometryMath';

export type GradientKind = 'linear' | 'cylindrical' | 'spherical' | 'hemispherical' | 'curved';

/** Gradient block of the hatch entity data bag. */
export interface GradientData {
    kind: GradientKind;
    inverted: boolean;
    color1: string;
    color2: string;
    singleColor: boolean;
    /** 0..1 — one-color mode blends color2 toward white by tint. */
    tint: number;
    /** Radians, the gradient axis direction (linear family). */
    angle: number;
    /** Symmetric (mirrored) distribution about the center. */
    centered: boolean;
}

/** OCS DXF gradient name (from_name contract: INV prefix + kind substring). */
export function gradientDxfName(g: GradientData): string {
    const base: Record<GradientKind, string> = {
        linear: 'LINEAR',
        cylindrical: 'CYLINDER',
        spherical: 'SPHERE',
        hemispherical: 'HEMISPHERE',
        curved: 'CURVE',
    };
    return `${g.inverted ? 'INV-' : ''}${base[g.kind]}`;
}

/** Parse a hex color to [r,g,b] 0..1 (fallback black). */
function parseColor(hex: string): [number, number, number] {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return [0, 0, 0];
    const n = parseInt(m[1], 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function mix(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Color2 for one-color mode: the tint blends toward white (OCS shade_tint). */
export function effectiveColors(g: GradientData): { c1: [number, number, number]; c2: [number, number, number] } {
    const c1 = parseColor(g.color1);
    const c2 = g.singleColor
        ? mix(c1, [1, 1, 1], Math.max(0, Math.min(1, g.tint)))
        : parseColor(g.color2);
    return { c1, c2 };
}

/** Shader t for a sample point (world XY). */
export function gradientT(
    g: GradientData, x: number, y: number,
    ctx: { axis: { x: number; y: number }; projMin: number; projRange: number; center: Pt3; radialRange: number }
): number {
    let t: number;
    if (g.kind === 'spherical' || g.kind === 'hemispherical') {
        const d = Math.hypot(x - ctx.center[0], y - ctx.center[1]);
        t = Math.max(0, Math.min(1, d / Math.max(ctx.radialRange, 1e-9)));
        if (g.kind === 'hemispherical') t = Math.sqrt(t);
    } else {
        const proj = (x - ctx.center[0]) * ctx.axis.x + (y - ctx.center[1]) * ctx.axis.y;
        t = (proj - ctx.projMin) / Math.max(ctx.projRange, 1e-9);
        t = Math.max(0, Math.min(1, t));
        if (g.kind === 'cylindrical') t = 1 - Math.abs(2 * t - 1);
        if (g.kind === 'curved') t = t * t;
    }
    if (g.inverted) t = 1 - t;
    return t;
}

/** Pre-computed projection context from the boundary ring. */
export function gradientContext(g: GradientData, ring: Pt3[]) {
    const xs = ring.map((p) => p[0]);
    const ys = ring.map((p) => p[1]);
    const min: Pt3 = [Math.min(...xs), Math.min(...ys), 0];
    const max: Pt3 = [Math.max(...xs), Math.max(...ys), 0];
    const center: Pt3 = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, 0];
    const axis = { x: Math.cos(g.angle), y: Math.sin(g.angle) };
    let projMin = Infinity, projMax = -Infinity, radialRange = 0;
    ring.forEach((p) => {
        const proj = (p[0] - center[0]) * axis.x + (p[1] - center[1]) * axis.y;
        projMin = Math.min(projMin, proj);
        projMax = Math.max(projMax, proj);
        radialRange = Math.max(radialRange, Math.hypot(p[0] - center[0], p[1] - center[1]));
    });
    // Centered mode mirrors the projection about the middle of the range.
    if (g.centered) projMin = -((projMax - projMin) / 2);
    return { axis, projMin, projRange: projMax - projMin, center, radialRange, min, max };
}

/** 256x1 canvas strip of the gradient (a texture for the hatch mesh). */
export function gradientStripCanvas(g: GradientData): HTMLCanvasElement {
    const w = 256;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = 1;
    const ctx2d = canvas.getContext('2d')!;
    const img = ctx2d.createImageData(w, 1);
    const { c1, c2 } = effectiveColors(g);
    const radial = g.kind === 'spherical' || g.kind === 'hemispherical';
    // One column per t — the mesh UVs carry the geometric projection, the
    // strip carries only the profile + color ramp.
    for (let i = 0; i < w; i++) {
        let t = i / (w - 1);
        if (radial) t = g.kind === 'hemispherical' ? Math.sqrt(t) : t;
        else if (g.kind === 'cylindrical') t = 1 - Math.abs(2 * t - 1);
        else if (g.kind === 'curved') t = t * t;
        if (g.inverted) t = 1 - t;
        const rgb = radial ? mix(c2, c1, t) : mix(c1, c2, t);
        const o = i * 4;
        img.data[o] = Math.round(rgb[0] * 255);
        img.data[o + 1] = Math.round(rgb[1] * 255);
        img.data[o + 2] = Math.round(rgb[2] * 255);
        img.data[o + 3] = 255;
    }
    ctx2d.putImageData(img, 0, 0);
    return canvas;
}

/** Data URL preview (dialog thumbnails). */
export function gradientPreviewUrl(g: GradientData): string {
    return gradientStripCanvas(g).toDataURL('image/png');
}
